import { useCallback, useState } from "react";

export const useNumericKeyboardWithSymbol = (initialAmount: string = "0") => {
  const [amount, setAmount] = useState<string>(initialAmount || "0");
  const [isKeyboardPressedForTheFirstTime, setIsKeyboardPressedForTheFirstTime] = useState<boolean>(false);

  const handleKeyboardPress = useCallback((value: string) => {
    if (value === 'delete') {
      setAmount(prev => {
        if (prev.length <= 1) {
          return "0"; // Reset to zero if deleting the last digit
        }
        return prev.slice(0, -1);
      });
      return;
    }

    if (value === '000') {
      setAmount(prev => {
        // Don't add zeros after decimal point
        if (prev.includes('.') || prev.includes(',')) {
          // Handle both period and comma as decimal separators
          const parts = prev.split(/[.,]/);
          return `${parts[0]}000.${parts[1] || ''}`;
        }
        return `${prev}000`;
      });
      return;
    }

    // Period
    if (value === '.') {
      setAmount(prev => {
        // Only add decimal if it doesn't already exist
        if (prev.includes('.')) {
          return prev;
        }
        return `${prev}.`;
      });
      return;
    }

    // Comma
    if (value === ',') {
      setAmount(prev => {
        // Only add decimal if it doesn't already exist
        if (prev.includes(',')) {
          return prev;
        }
        // Use period as the standard decimal separator
        return `${prev},`;
      });
      return;
    }

    // For regular digits
    setAmount(prev => {
      // If this is the first keypress and prev is "0", replace it
      if (prev === "0" && value !== "." && value !== ",") {
        return value;
      }
      // Otherwise append the digit
      return `${prev}${value}`;
    });

    setIsKeyboardPressedForTheFirstTime(true);
  }, []);

  // For compatibility, provide numeric version too
  // Use a regex replace to convert any commas to periods before parsing
  const numericAmount = parseFloat(amount.replace(',', '.'));

  return {
    amount,
    numericAmount,
    setAmount,
    handleKeyboardPress,
    isKeyboardPressedForTheFirstTime
  };
};